/**
 * DisciplineCanvasEditor — generic 2D palette + SVG canvas editor.
 *
 * Implements the eleven palette-based discipline editors per
 * `Capexara Process Industry_ProcessPlant_Complete_Architecture.md` §6.3:
 *   hazarea E.1, sld E.3, earthing E.4, cable_tray E.5,
 *   control_arch F.1, foundation G.2, steel_ga G.3, rc_detail G.4,
 *   fire_water H.1, detection H.2, suppression H.3.
 *
 * Persists to pdo.disciplines.<key>.  Palette data is loaded once from
 * GET /api/discipline/palettes; canvas data from
 * GET/PUT /api/projects/{id}/disciplines/{key}.
 */
import React, { useEffect, useRef, useState } from "react";
import { Loader2, Save, Trash2, Pointer, Move, Box, Square } from "lucide-react";
import api, { formatErr } from "../lib/api";

// Disciplines that support a 2D↔3D toggle per spec §6.1
const SUPPORTS_3D = new Set(["steel_ga", "cable_tray", "fire_water"]);

export default function DisciplineCanvasEditor({ projectId, disciplineKey }) {
  const [palette, setPalette] = useState(null);
  const [items, setItems] = useState([]);
  const [armed, setArmed] = useState(null);   // symbol object user picked
  const [sel, setSel] = useState(null);       // selected item id
  const [drag, setDrag] = useState(null);
  const [busy, setBusy] = useState(false);
  const [title, setTitle] = useState("");
  const [toast, setToast] = useState(null);
  const [view, setView] = useState("2d");     // 2d | 3d (isometric)
  const [mode, setMode] = useState(null);     // Fire Water: "layout" | "pid"
  const svgRef = useRef(null);
  const supports3D = SUPPORTS_3D.has(disciplineKey);
  const isFireWater = disciplineKey === "fire_water";

  const show = (t, kind = "ok") => {
    setToast({ t, kind });
    setTimeout(() => setToast(null), 3500);
  };

  useEffect(() => {
    (async () => {
      setBusy(true);
      try {
        const [p, c] = await Promise.all([
          api.get("/discipline/palettes"),
          api.get(`/projects/${projectId}/disciplines/${disciplineKey}`),
        ]);
        setPalette(p.data[disciplineKey]);
        setTitle(p.data[disciplineKey]?.title || disciplineKey);
        setItems(c.data?.items || []);
      } catch (e) {
        show(formatErr(e?.response?.data?.detail) || e.message, "err");
      } finally {
        setBusy(false);
      }
    })();
  }, [projectId, disciplineKey]);

  const symbolById = (id) => (palette?.symbols || []).find((s) => s.id === id);

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/projects/${projectId}/disciplines/${disciplineKey}`,
                     { items, canvas_svg: null });
      show(`${items.length} items saved`);
    } catch (e) {
      show(formatErr(e?.response?.data?.detail) || e.message, "err");
    } finally { setBusy(false); }
  };

  const onCanvasClick = (e) => {
    if (!armed) return;
    const r = svgRef.current.getBoundingClientRect();
    const x = ((e.clientX - r.left) / r.width) * 1000;
    const y = ((e.clientY - r.top) / r.height) * 600;
    const id = `${disciplineKey}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
    const tag = armed.tag_prefix ? `${armed.tag_prefix}-${items.length + 101}` : "";
    setItems([...items, { id, sym: armed.id, x, y, rot: 0, tag, props: {} }]);
    setArmed(null);
  };

  const onItemDown = (e, it) => {
    e.stopPropagation();
    setSel(it.id);
    const r = svgRef.current.getBoundingClientRect();
    const px = ((e.clientX - r.left) / r.width) * 1000;
    const py = ((e.clientY - r.top) / r.height) * 600;
    setDrag({ id: it.id, dx: px - it.x, dy: py - it.y });
  };

  const onMouseMove = (e) => {
    if (!drag) return;
    const r = svgRef.current.getBoundingClientRect();
    const x = ((e.clientX - r.left) / r.width) * 1000 - drag.dx;
    const y = ((e.clientY - r.top) / r.height) * 600 - drag.dy;
    setItems((prev) => prev.map((it) => it.id === drag.id ? { ...it, x, y } : it));
  };

  const onMouseUp = () => setDrag(null);

  const del = () => {
    if (!sel) return;
    setItems(items.filter((it) => it.id !== sel));
    setSel(null);
  };

  return (
    <div data-testid={`disc-editor-${disciplineKey}`}
         style={{ display: "flex", height: "calc(100vh - 280px)", minHeight: 600, gap: 8 }}>
      {/* Palette */}
      <div style={{
        width: 260, minWidth: 260, overflow: "auto",
        background: "var(--pf-surface)", border: "1px solid var(--pf-border)",
        padding: 10, borderRadius: 6,
      }}>
        <div className="pf-overline" style={{ marginBottom: 8 }}>
          {title} — Palette ({palette?.symbol_count || 0})
        </div>
        {busy && !palette && <Loader2 className="pf-spin" size={14} />}
        {palette && (palette.categories || []).map((cat) => {
          // Fire Water: filter palette by active mode (layout vs P&ID)
          if (isFireWater && mode === "layout" && cat === "FireWater_PID") return null;
          if (isFireWater && mode === "pid" && cat === "FireWater_Layout") return null;
          const syms = (palette.symbols || []).filter((s) => s.category === cat);
          if (!syms.length) return null;
          return (
            <div key={cat} style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 10, color: "var(--pf-text-dim)",
                             fontFamily: "'JetBrains Mono', monospace",
                             marginBottom: 4 }}>{cat.replace(/_/g, " ")}</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 4 }}>
                {syms.map((s) => (
                  <button
                    key={s.id}
                    data-testid={`disc-palette-${s.id}`}
                    onClick={() => setArmed(armed?.id === s.id ? null : s)}
                    title={s.name}
                    style={{
                      background: armed?.id === s.id ? "rgba(255,176,0,0.18)" : "var(--pf-surface-2)",
                      border: `1px solid ${armed?.id === s.id ? "var(--pf-accent)" : "var(--pf-border)"}`,
                      borderRadius: 3,
                      padding: 4, cursor: "pointer",
                      display: "flex", flexDirection: "column", alignItems: "center", gap: 2,
                    }}
                  >
                    <svg width={60} height={36} viewBox="0 0 80 60">
                      <g dangerouslySetInnerHTML={{ __html: s.svg }} />
                    </svg>
                    <span style={{ fontSize: 8, color: "var(--pf-text-dim)",
                                    textAlign: "center", lineHeight: 1.1,
                                    maxWidth: "100%", overflow: "hidden",
                                    textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                          title={s.name}>
                      {s.name}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Canvas + toolbar */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column",
                    background: "var(--pf-surface)", border: "1px solid var(--pf-border)",
                    borderRadius: 6, overflow: "hidden" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8,
                       padding: "6px 10px",
                       borderBottom: "1px solid var(--pf-border)",
                       background: "var(--pf-surface-2)" }}>
          <span className="pf-overline" style={{ margin: 0 }}>{title}</span>
          <span style={{ fontSize: 11, color: "var(--pf-text-dim)",
                          fontFamily: "'JetBrains Mono', monospace" }}>
            · {items.length} placed {armed ? `· armed: ${armed.name}` : ""}
          </span>
          {isFireWater && (
            <div style={{ display: "flex", gap: 2, marginLeft: 6 }}>
              <button data-testid="disc-fw-mode-layout"
                       onClick={() => setMode(mode === "layout" ? null : "layout")}
                       style={{
                         fontSize: 10, padding: "2px 8px",
                         background: mode === "layout" ? "var(--pf-accent)" : "var(--pf-surface)",
                         color: mode === "layout" ? "#0E1218" : "var(--pf-fg)",
                         border: `1px solid ${mode === "layout" ? "var(--pf-accent)" : "var(--pf-border)"}`,
                         borderRadius: 2, cursor: "pointer",
                       }}>Layout</button>
              <button data-testid="disc-fw-mode-pid"
                       onClick={() => setMode(mode === "pid" ? null : "pid")}
                       style={{
                         fontSize: 10, padding: "2px 8px",
                         background: mode === "pid" ? "var(--pf-accent)" : "var(--pf-surface)",
                         color: mode === "pid" ? "#0E1218" : "var(--pf-fg)",
                         border: `1px solid ${mode === "pid" ? "var(--pf-accent)" : "var(--pf-border)"}`,
                         borderRadius: 2, cursor: "pointer",
                       }}>P&amp;ID</button>
            </div>
          )}
          {supports3D && (
            <div style={{ display: "flex", gap: 2, marginLeft: 6 }}>
              <button data-testid={`disc-${disciplineKey}-view-2d`}
                       onClick={() => setView("2d")}
                       title="2D Plan view"
                       style={{
                         fontSize: 10, padding: "2px 8px",
                         background: view === "2d" ? "var(--pf-accent)" : "var(--pf-surface)",
                         color: view === "2d" ? "#0E1218" : "var(--pf-fg)",
                         border: `1px solid ${view === "2d" ? "var(--pf-accent)" : "var(--pf-border)"}`,
                         borderRadius: 2, cursor: "pointer",
                         display: "inline-flex", alignItems: "center", gap: 3,
                       }}><Square size={10}/> 2D</button>
              <button data-testid={`disc-${disciplineKey}-view-3d`}
                       onClick={() => setView("3d")}
                       title="3D isometric view"
                       style={{
                         fontSize: 10, padding: "2px 8px",
                         background: view === "3d" ? "var(--pf-accent)" : "var(--pf-surface)",
                         color: view === "3d" ? "#0E1218" : "var(--pf-fg)",
                         border: `1px solid ${view === "3d" ? "var(--pf-accent)" : "var(--pf-border)"}`,
                         borderRadius: 2, cursor: "pointer",
                         display: "inline-flex", alignItems: "center", gap: 3,
                       }}><Box size={10}/> 3D</button>
            </div>
          )}
          <div style={{ flex: 1 }} />
          <button className="btn btn-ghost" onClick={() => setArmed(null)}
                  data-testid={`disc-${disciplineKey}-cancel-arm`}
                  disabled={!armed}
                  style={{ fontSize: 11, padding: "4px 8px" }}>
            <Pointer size={12} /> Select
          </button>
          <button className="btn btn-ghost" onClick={del}
                  data-testid={`disc-${disciplineKey}-delete`}
                  disabled={!sel}
                  style={{ fontSize: 11, padding: "4px 8px", color: "#ef4444" }}>
            <Trash2 size={12} /> Delete
          </button>
          <button className="btn btn-primary" onClick={save} disabled={busy}
                  data-testid={`disc-${disciplineKey}-save`}
                  style={{ fontSize: 11, padding: "4px 10px" }}>
            {busy ? <Loader2 className="pf-spin" size={12}/> : <Save size={12}/>} Save
          </button>
        </div>

        <div style={{ flex: 1, position: "relative", overflow: "auto",
                       background: "var(--pf-canvas-bg, #0b0f15)" }}>
          <svg
            ref={svgRef}
            viewBox="0 0 1000 600"
            preserveAspectRatio="xMidYMid meet"
            onClick={onCanvasClick}
            onMouseMove={onMouseMove}
            onMouseUp={onMouseUp}
            onMouseLeave={onMouseUp}
            style={{
              width: "100%", height: "100%",
              cursor: armed ? "crosshair" : "default",
              backgroundImage: view === "3d"
                ? "linear-gradient(135deg, rgba(255,176,0,0.03) 1px, transparent 1px), linear-gradient(45deg, rgba(255,176,0,0.03) 1px, transparent 1px)"
                : "linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px)",
              backgroundSize: "20px 20px",
              transform: view === "3d" ? "matrix(0.866,0.5,-0.866,0.5,0,0)" : "none",
              transformOrigin: "center center",
              transition: "transform 300ms",
            }}
            data-testid={`disc-canvas-${disciplineKey}`}
          >
            {items.map((it) => {
              const sym = symbolById(it.sym);
              if (!sym) return null;
              return (
                <g
                  key={it.id}
                  transform={`translate(${it.x - 40}, ${it.y - 30}) rotate(${it.rot},40,30)`}
                  onMouseDown={(e) => onItemDown(e, it)}
                  style={{ cursor: "move" }}
                  data-testid={`disc-item-${it.id}`}
                >
                  <g dangerouslySetInnerHTML={{ __html: sym.svg }} />
                  {sel === it.id && (
                    <rect x="0" y="0" width="80" height="60" rx="2"
                          fill="none" stroke="#F26B1F" strokeWidth="1.5"
                          strokeDasharray="3,3" />
                  )}
                  {it.tag && (
                    <text x="40" y="74" textAnchor="middle" fill="#FFB000"
                           fontFamily="JetBrains Mono, monospace" fontSize="10"
                           pointerEvents="none">{it.tag}</text>
                  )}
                </g>
              );
            })}
          </svg>
          {!items.length && !busy && (
            <div style={{
              position: "absolute", inset: 0,
              display: "flex", alignItems: "center", justifyContent: "center",
              color: "var(--pf-text-dim)", fontFamily: "'JetBrains Mono', monospace",
              fontSize: 12, pointerEvents: "none",
            }}>
              <Move size={14} style={{ marginRight: 6 }} />
              Pick a symbol → click to place. Drag to move. Save to persist.
            </div>
          )}
        </div>
      </div>

      {toast && (
        <div data-testid={`disc-toast-${toast.kind}`} style={{
          position: "fixed", bottom: 28, right: 28,
          background: toast.kind === "ok" ? "rgba(34,197,94,0.2)" : "rgba(239,68,68,0.2)",
          color: toast.kind === "ok" ? "#86efac" : "#fca5a5",
          border: `1px solid ${toast.kind === "ok" ? "#22c55e" : "#ef4444"}`,
          padding: "8px 14px", fontSize: 12, borderRadius: 3, zIndex: 10001,
          fontFamily: "'JetBrains Mono', monospace",
        }}>{toast.t}</div>
      )}
    </div>
  );
}
